/**
 * How a camera fact is anchored inside a shot's own sentence.
 *
 * Split out of `shotPromptParts.js` when that file crossed its size limit, and it earns the
 * separation: this owns the marker vocabulary and the substitution rules, while the prompt
 * model owns how the resulting pieces are classified and ordered.
 */

/**
 * The facts a shot's own sentence may anchor, and the manifest field each names.
 *
 * A marker is an ANCHOR, never a value. `{{framing}}` says "the framing belongs HERE"; the
 * manifest still owns what the framing is. That is what lets a swapped value re-render in
 * place without anyone searching the prose for the old words.
 */
export const INTENT_MARKER_SLOTS = Object.freeze({
  framing: { slotPath: 'camera.framing', field: 'framing' },
  cameraMovement: { slotPath: 'camera.movement', field: 'movement' },
  lens: { slotPath: 'camera.lens', field: 'lens' },
  blocking: { slotPath: 'direction.blocking', field: 'blocking' },
  device: { slotPath: 'direction.device', field: 'device' },
});
/**
 * A cast member's acting note, anchored by ROLE rather than by position.
 *
 * `{{cast[role:the clerk].performance}}` says "the clerk's acting note belongs HERE". The
 * five slots above are keyed by a flat path because each names one fact on the manifest;
 * a note names one fact on ONE CAST MEMBER, and that member needs an identity the anchor
 * can survive on.
 *
 * ⛔ THE INDEX IS NOT THAT IDENTITY. `syncStoryboardShotManifests` rebuilds the cast array
 * from `elementAssignments` on every pass, so `cast[0]` is a POSITION: rebind or reorder
 * and an index-keyed anchor silently starts describing a different character. The ROLE is
 * what the manifest itself already treats as the note's identity -- the sync carries a
 * performance across a recast by looking the previous entry up in a role-keyed Map, and a
 * test pins it. Keying the anchor the same way makes the prose agree with the store rather
 * than inventing a second notion of who a note is about.
 *
 * The BRACKET form is deliberate and load-bearing, not cosmetic. Several surfaces dispatch
 * a slot path with `slotPath.split('.')` and write `manifest[group][key]`. A dotted
 * `cast.<role>.performance` splits to `group === 'cast'`, and spreading the cast ARRAY into
 * an object produces a non-array that `normalizeCastBindings` collapses to `[]` -- one chip
 * pick would delete every cast member on the shot. The bracket form splits to the unknown
 * top-level key `cast[role:the clerk]`, which the closed manifest normalizer simply drops.
 * The grammar is what makes the legacy dispatchers fail SAFE.
 */
const CAST_PERFORMANCE_ROLE_CHARS = '[^\\]{}\\n]+';
const CAST_PERFORMANCE_SLOT_PATH = /^cast\[role:([^\]{}\n]+)\]\.performance$/;

/**
 * Which roles may be anchored at all.
 *
 * A role is an arbitrary user- or agent-authored string, so it can contain the very
 * characters the token grammar uses as delimiters. Rather than escape them -- which would
 * make the token no longer round-trip byte-exactly, the property the whole anchor design
 * rests on -- an unrepresentable role simply does not anchor, and the pick takes the
 * defined no-caret fallback: value written, no token, trailing clause. Today's behaviour.
 *
 * Spaces MUST stay legal: the role this app actually exercises is `the clerk`.
 */
function isAnchorableRole(role) {
  if (typeof role !== 'string') return false;
  const trimmed = role.trim();
  if (trimmed === '') return false;
  return !/[\]{}\n]/.test(trimmed);
}

/**
 * ONE pattern, two alternations, because there is one marker vocabulary.
 *
 * Group 1 is a flat slot key; group 2 is a cast role. A second `RegExp` scanned separately
 * would be a second owner of "where are the anchors", and the two would disagree the first
 * time one of them learned a rule -- which is exactly how the read path and the write path
 * came to disagree about the chip catalog before.
 */
const INTENT_MARKER_PATTERN = new RegExp(
  `\\{\\{(?:(${Object.keys(INTENT_MARKER_SLOTS).join('|')})`
  + `|cast\\[role:(${CAST_PERFORMANCE_ROLE_CHARS})\\]\\.performance)\\}\\}`,
  'g',
);

/** The slot path a role's acting note is addressed by, or null when the role cannot anchor. */
export function castPerformanceSlotPath(role) {
  if (!isAnchorableRole(role)) return null;
  return `cast[role:${role.trim()}].performance`;
}

/**
 * The role a cast-performance slot path names, or null when it is not one.
 *
 * ⛔ The SAME gate that mints a path decides whether one parses. `[^\]{}\n]+` happily
 * matches `   `, so a whitespace-only role trimmed to `''` used to come back as an empty
 * STRING -- falsy, but not null, so every `role !== null` branch downstream accepted it and
 * addressed a member that cannot exist.
 */
export function getCastPerformanceSlotRole(slotPath) {
  if (typeof slotPath !== 'string') return null;
  const match = CAST_PERFORMANCE_SLOT_PATH.exec(slotPath);
  if (!match) return null;
  return isAnchorableRole(match[1]) ? match[1].trim() : null;
}

/**
 * Does this slot path address the CAST at all, in any shape?
 *
 * ⛔ THE BOUNDARY, NOT THE TWO SHAPES WE HAPPEN TO MINT. A surface that splits a path on
 * `.` and writes `manifest[group][key]` destroys the whole cast for ANY path whose group is
 * `cast` -- spreading the array into an object gives a non-array that `normalizeCastBindings`
 * collapses to `[]`. Guarding only the role and index forms left the bare `cast.performance`
 * open, which is the exact path the destruction test uses: the test demonstrated a loss the
 * guard beside it did not actually prevent.
 *
 * So this asks the structural question. Anything addressing the cast is refused by the
 * surfaces that cannot write it safely, whatever shape arrives next.
 */
export function isCastSlotPath(slotPath) {
  if (typeof slotPath !== 'string') return false;
  return slotPath === 'cast' || slotPath.startsWith('cast.') || slotPath.startsWith('cast[');
}

/**
 * The marker token that anchors a slot path -- the ONLY place a token is composed.
 *
 * The five flat slots read their token from the derived map; a cast path composes one from
 * its role. A caller that built `` `{{${key}}}` `` itself would be a second copy of the
 * vocabulary, which is the drift this module exists to prevent.
 */
export function markerTokenForSlotPath(slotPath) {
  const flat = SLOT_PATH_MARKER_TOKENS[slotPath];
  if (flat) return flat;
  const role = getCastPerformanceSlotRole(slotPath);
  return role ? `{{cast[role:${role}].performance}}` : null;
}

/** slotPath -> { group, field }. The render gate and the write dispatch both read THIS. */
export const ANCHORABLE_SLOT_FIELDS = Object.freeze(Object.fromEntries(
  Object.values(INTENT_MARKER_SLOTS)
    .map(({ slotPath, field }) => [slotPath, { group: slotPath.split('.')[0], field }]),
));

/**
 * slotPath -> the marker token that anchors it, e.g. `camera.framing` -> `{{framing}}`.
 *
 * DERIVED from `INTENT_MARKER_SLOTS`, never restated. A UI file that wrote its own
 * `` `{{${key}}}` `` would be a second copy of the vocabulary, and the two would drift the
 * first time a slot was added -- which is exactly how the read path and the write path came
 * to disagree about the chip catalog before.
 */
export const SLOT_PATH_MARKER_TOKENS = Object.freeze(Object.fromEntries(
  Object.entries(INTENT_MARKER_SLOTS).map(([key, { slotPath }]) => [slotPath, `{{${key}}}`]),
));

/**
 * Is this slot one the sentence can anchor at all?
 *
 * A ROLE-keyed cast path is anchorable; an INDEX-keyed one (`cast[0].performance`) is not,
 * and never becomes so. The index remains a live address -- the setter takes it and the `+`
 * row is built from it -- but it is resolved at the moment of use and never written into
 * prose, because prose outlives the array position it would name.
 */
export function isAnchorableSlotPath(slotPath) {
  if (Object.prototype.hasOwnProperty.call(SLOT_PATH_MARKER_TOKENS, slotPath)) return true;
  return getCastPerformanceSlotRole(slotPath) !== null;
}

/**
 * Split authored text around its markers WITHOUT resolving them.
 *
 * The twin of `splitIntentByMarkers` below, and deliberately not the same function. That one
 * answers "what does this compile to", so it needs a manifest and substitutes values. This
 * one answers "where are the anchors in this string", which is what an EDITOR needs: the
 * editor renders the marker as a chip that resolves its own value live, so a splitter that
 * baked the value in would freeze it at parse time.
 *
 * Shaped like `splitRoleAnchors` on purpose -- marker-free text returns exactly one segment,
 * byte-identical to the input, so a caller can treat "no anchors" as the ordinary path.
 */
export function splitPromptMarkers(text) {
  const source = typeof text === 'string' ? text : '';
  if (!source.includes('{{')) return [{ type: 'text', text: source }];

  const segments = [];
  let cursor = 0;
  INTENT_MARKER_PATTERN.lastIndex = 0;
  let match = INTENT_MARKER_PATTERN.exec(source);
  while (match) {
    // `token` is the matched text, carried so no consumer has to reconstruct its length.
    // A cast anchor has no flat vocabulary `key`, and a consumer computing `key.length + 4`
    // to find the token's end threw on `undefined` the moment one was typed -- the marker
    // shape has two variants, so the LENGTH has to come from the match rather than from a
    // field only one variant has.
    //
    // The role is TRIMMED here, matching what the compile path does when it resolves. Two
    // readers disagreeing about `{{cast[role: the clerk ].performance}}` meant it compiled
    // using the clerk's note while the editor showed a placeholder and silently dropped
    // menu writes -- one fact, two normalizations. Canonicalizing at the vocabulary owner
    // makes the next edit heal the token.
    // ⛔ A ROLE THAT CANNOT BE MINTED CANNOT BE PARSED. `[^\]{}\n]+` matches `   `, and
    // trimming that produced `cast[role:].performance` -- a path nothing can re-parse, so
    // the node built from it had an empty `getTextContent()` and DELETED the user's own
    // token from the prompt on the next commit. Same gate both directions, and anything
    // that fails it stays literal text, exactly like any other brace pair the app does not
    // understand.
    if (!match[1] && !isAnchorableRole(match[2])) {
      match = INTENT_MARKER_PATTERN.exec(source);
      continue;
    }
    if (match.index > cursor) {
      segments.push({ type: 'text', text: source.slice(cursor, match.index) });
    }
    segments.push(match[1]
      ? {
        type: 'marker',
        key: match[1],
        slotPath: INTENT_MARKER_SLOTS[match[1]].slotPath,
        token: match[0],
      }
      : {
        type: 'marker',
        slotPath: `cast[role:${match[2].trim()}].performance`,
        token: match[0],
      });
    cursor = match.index + match[0].length;
    match = INTENT_MARKER_PATTERN.exec(source);
  }
  if (cursor < source.length) {
    segments.push({ type: 'text', text: source.slice(cursor) });
  }

  return segments.length > 0 ? segments : [{ type: 'text', text: source }];
}

/**
 * Split an intent around its markers into alternating atoms: authored `intent` prose and
 * authored manifest-slot values. Marker-free intent returns exactly one atom, byte-identical
 * to what it produced before markers existed.
 *
 * A marker whose manifest value is null compiles to NOTHING, and the space that would have
 * been left doubled is collapsed. An unresolved `{{framing}}` must never reach the model --
 * that is the load-bearing rule here; the tidied spacing is best effort.
 */
export function splitIntentByMarkers(intentText, source) {
  if (!intentText.includes('{{')) {
    return { segments: [{ slotPath: 'intent', text: intentText }], anchored: new Set() };
  }

  const segments = [];
  const anchored = new Set();
  let cursor = 0;
  INTENT_MARKER_PATTERN.lastIndex = 0;
  let match = INTENT_MARKER_PATTERN.exec(intentText);
  while (match) {
    // The compile twin of the parse gate above. Both readers must agree about which tokens
    // are real, or a token the editor leaves as prose gets substituted here -- the same
    // two-normalizations split that made a space-bearing role compile one way and render
    // another.
    if (!match[1] && !isAnchorableRole(match[2])) {
      match = INTENT_MARKER_PATTERN.exec(intentText);
      continue;
    }
    if (match.index > cursor) {
      segments.push({ slotPath: 'intent', text: intentText.slice(cursor, match.index) });
    }
    let slotPath;
    let sourceValue;
    if (match[1]) {
      const slot = INTENT_MARKER_SLOTS[match[1]];
      slotPath = slot.slotPath;
      // `split('.')` is safe HERE and nowhere near a cast path: these five are flat.
      sourceValue = source?.[slotPath.split('.')[0]]?.[slot.field];
    } else {
      // Resolve through the ROLE, byte-exact after trim, exactly as the sync's role Map
      // matches. Lowest matching index wins: the assignment map is keyed by role so it
      // collapses duplicates before the cast is built, but a preserved unmanaged entry
      // could in principle repeat one, and a stated rule beats whichever order a rebuild
      // happened to produce.
      const role = match[2].trim();
      slotPath = `cast[role:${role}].performance`;
      const cast = Array.isArray(source?.cast) ? source.cast : [];
      const entry = cast.find((member) => (
        typeof member?.role === 'string' && member.role.trim() === role
      ));
      sourceValue = entry?.performance;
    }
    const value = typeof sourceValue === 'string' ? sourceValue.trim() : '';
    // Anchored either way. A null value still means the fact is SPOKEN FOR here, so the
    // bracket tag must not also state it -- otherwise clearing a value would silently move
    // it back to the tag rather than removing it.
    anchored.add(slotPath);
    segments.push({ slotPath, text: value });
    cursor = match.index + match[0].length;
    match = INTENT_MARKER_PATTERN.exec(intentText);
  }
  if (cursor < intentText.length) {
    segments.push({ slotPath: 'intent', text: intentText.slice(cursor) });
  }

  // Collapse the gap an empty substitution leaves behind: "A {{framing}} of a dog." with no
  // framing would otherwise compile to "A  of a dog.".
  for (let i = 0; i < segments.length; i += 1) {
    if (segments[i].text !== '' || segments[i].slotPath === 'intent') continue;
    const before = segments[i - 1];
    const after = segments[i + 1];
    if (before?.text.endsWith(' ') && after?.text.startsWith(' ')) {
      after.text = after.text.slice(1);
    }
  }

  // An intent of nothing but an unresolved marker leaves no segments at all; the caller
  // treats that the same as an empty intent.
  return { segments: segments.filter((segment) => segment.text !== ''), anchored };
}


/**
 * Render a marked string for a consumer that cannot use the atom projections.
 *
 * The projections (`buildShotPromptParts`, `buildShotPromptText`) already resolve markers,
 * and every consumer that can should use them -- they carry the slot attribution too. This
 * exists for the ones that hold a bare string: a brief being assembled for a model, a value
 * copied onto a take, anything that would otherwise ship `{{framing}}` verbatim to a screen
 * or to a paid generation.
 *
 * Same rules as the compile path: a known marker becomes its manifest value, an unresolved
 * one becomes nothing with the doubled space collapsed, and an unrecognised brace pair is
 * left exactly as written because prose the app does not understand is still the user's.
 */
export function renderShotPromptMarkers(text, manifest = null) {
  if (typeof text !== 'string' || !text.includes('{{')) return text;
  const { segments } = splitIntentByMarkers(text, manifest || {});
  return segments.map((segment) => segment.text).join('');
}
